import React, { useState } from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import CardMedia from "@mui/material/CardMedia";
import DtvSpinner from "@directv-digital/dds.ui.dtv-spinner";
import DtvTooltip from "@directv-digital/dds.ui.dtv-tooltip";
import ZipcodeModule from "src/components/video-lab/zipcode-module";
import { getLinkClickAttrs, modalLinkDestination, modalLinkPosition, usePageUrl } from "src/utils/link-click";

// WAT-12738 (AC7/AC8): the identifier of the local-channels modal — its opener reports
// page URL + "/local-channels", its close X "modal_local-channels".
const LOCALS_MODAL_ID = "local-channels";

export interface LocalsOnlyModalProps {
  productTypeSatellite: boolean;
  linkText?: string;
  title?: string;
  subTitle?: string;
  savingsText?: string;
  tooltip?: any;
}

export interface ChnlCmpToolOptions {
  breakpoint?: number;
  id?: string;
  index?: number;
  isChannelLineUpPage?: boolean;
  packageBgColor?: any;
  edspFlow?: boolean;
  modalView?: boolean;
  pageName?: string;
  tab?: string;
  lang?: string;
  category?: string;
  pkg?: string;
  cmprPkg?: string;
  modalSrc?: string;
  showCTA?: string;
  channelsQP?: string;
  isLocal?: string;
}

export const LocalsOnlyModal = (props: LocalsOnlyModalProps) => {
  const pageUrl = usePageUrl();
  try {
    const [open, setOpen] = useState<boolean>(false);
    const [localChnlsData, setLocalChnlsData] = useState<any>([]);
    const [_apiError, setApiError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const {
      productTypeSatellite,
      savingsText = "($12/mo. in savings with no locals)",
      linkText = "Check your local channels here",
      title = "Here's a list of your local channels",
      subTitle = "Local channels are not available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them."
    } = props;
    const updateIsLocalsEligible = (channels:{
        localChannels?: any[];
        rsnChannels?: any[];
      } | null) => {
      if (channels?.localChannels?.length > 0) {
        const sortedChnls = channels?.localChannels?.sort((chnlA, chnlB) => (chnlA.label.toLowerCase() > chnlB.label.toLowerCase() ? 1 :
          chnlB.label.toLowerCase() > chnlA.label.toLowerCase() ? -1 :
            0));
        setLocalChnlsData(sortedChnls);
      }
    };

    const handleOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    return (
      <DtvBox>
        <DtvBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "row"
          }}
        >
          <DtvBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: {
 xs: "column",
sm: "row",
md: "row"
}
            }}
          >
            <DtvText
              content={savingsText}
              variant="body2"
              sx={{
                fontSize: {
 xs: "14px",
sm: "16px",
md: "16px"
},
                color: "#000"
              }}
            />
            <DtvButton
              googleAnalyticsData={getLinkClickAttrs(linkText, "body", modalLinkDestination(pageUrl, LOCALS_MODAL_ID))}
              label={linkText}
              mode="dark"
              variant="uxtext"
              sx={{
                paddingLeft: {
 sm: "5px",
md: "5px"
}
              }}
              onClick={handleOpen}
            />
          </DtvBox>
          <DtvTooltip
            content={props?.tooltip?.content}
            placement="top"
            sx={{
 marginLeft: "5px",
cursor: "pointer"
}}
          />
        </DtvBox>
        <DtvModal closeButtonProps={{ "data-linkposition": modalLinkPosition(LOCALS_MODAL_ID) }} handleClose={handleClose} open={open}
          modalBodyStyle={{
            marginRight: "0px !important",
            marginLeft: "0px !important",
            '&::-webkit-scrollbar': {
              width: '8px !important',
              height: '8px !important',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgb(220, 222, 227)',
              borderRadius: '20px',
              backgroundClip: 'unset !important',
              minHeight: '50px',
            },
            '&::-webkit-scrollbar-track': {
              boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 6px inset',
            },
          }}
        >
          <DtvBox
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingLeft: { xs: "20px", sm: "32px", md: "56px" },
              paddingRight: { xs: "20px", sm: "32px", md: "56px" },
            }}
            id="localonlyModal"
          >
            {loading && <DtvSpinner open  msg="" />}
            <DtvText
              content={title}
              variant="subhead1medium"
              sx={{
 marginBottom: "12px",
textAlign: "center"
}}
            />
            <DtvText
              content={subTitle}
              variant="subhead2"
              sx={{
                marginBottom: "12px",
                textAlign: "center",
                fontFamily: {
                  xs: "roboto-regular",
                  sm: "roboto-regular",
                  md: "PFDinReg"
                },
                fontSize: {
 xs: "16px",
sm: "16px",
md: "24px"
}
              }}
            />
            <DtvBox>
              <ZipcodeModule
                id="PC-topper-zipcode"
                mode="light"
                position="center"
                onLocalRsnChnlsConfirmed={updateIsLocalsEligible}
                onLocalRsnChnlsApiError={(err: string) => {
                  setApiError(err ? true : false);
                }}
                onLocalRsnChnlApiLoading={(isLoading: boolean) => {
                  setLoading(isLoading);
                }}
                localRsnApiOptions={{
                  stream: productTypeSatellite === false,
                  satellite: productTypeSatellite === true,
                  mergeProducts: false,
                  local: true,
                  rsn: false
                }}
                ciConfig={{
                  displayText: "Displaying channels for ",
                  zipCodeLabel: "ZIP Code",
                  ctaLabel: "Update"
                }}
              />
            </DtvBox>
            <DtvBox>
              <Table id="table">
                <TableHead id="tableHeader">
                  <TableRow>
                    <TableCell
                      sx={{
 padding: 0,
textAlign: "center",
width: "50%"
}}
                    >
                      <DtvText
                        color="#fff"
                        content="Network"
                        sx={{
                          backgroundColor: "#102641",
                          padding: {
                            xs: "20px 0px 22px 0px",
                            sm: "12px 0px 12px 0px",
                            md: "18px 0px 18px 0px"
                          },
                          border: "1px solid #D1D1D1"
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
 padding: 0,
textAlign: "center",
width: "50%"
}}
                    >
                      <DtvText
                        color="#000000"
                        content="Affiliate"
                        sx={{
                          backgroundColor: "#EAF1FA",
                          padding: {
                            xs: "20px 0px 22px 0px",
                            sm: "12px 0px 12px 0px",
                            md: "18px 0px 18px 0px"
                          },
                          border: "1px solid #D1D1D1"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody id="tableBody">
                {localChnlsData.map((channel: any, index: number) => (
                      <TableRow id={`${index}-tableBodyRow`} key={index}>
                        <TableCell
                          id="tableCell-1"
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justiyContent: "center",
                            alignItems: "center",
                            borderRight: "1px solid #D1D1D1",
                            padding: "31px, 53px, 31px, 53px"
                          }}
                        >
                          <CardMedia
                            alt="logo"
                            component="img"
                            id="channelLogo"
                            src={channel?.logo}
                            width="56px"
                            sx={{
 height: "56px",
width: "56px"
}}
                          />
                          <DtvText
                            color="#000"
                            content={channel?.label}
                            variant="body1"
                          />
                          <DtvText
                            color="#000"
                            content={`Channel ${channel?.chnlNum}`}
                            variant="body1"
                          />
                        </TableCell>
                        <TableCell
                          id="tableCell-2"
                          sx={{
                            textAlign: "center",
                            padding: "31px, 53px, 31px, 53px"
                          }}
                        >
                          <DtvText content={channel?.callSign} />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </DtvBox>
          </DtvBox>
        </DtvModal>
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="popupmodal" error={error} />;
  }
};
